import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import EuroOutlinedIcon from '@mui/icons-material/EuroOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import moment from 'moment';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import {
  REDLINK_TARGET_LABEL,
  RedlinkDetailResponse,
  RedlinkEvaluation,
  RedlinkEvaluationResponse,
  RedlinkTargetType,
  RedlinkTimelineResponse,
} from '../../helpers/redlinkData';

const HEADER_CELL = { backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' };

const fmtInt = (n: number) => formatNumber(n || 0, 0);
const fmtPct = (n: number) => `${formatNumber(n || 0, 2)} %`;
const fmtEur = (n: number) => `${formatNumber(n || 0, 2)} €`;
const fmtDate = (s: string | null) => (s ? moment(s).format('DD.MM.YYYY') : '–');

const StatCard: React.FC<{
  title: string;
  value: string;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, icon, color = '#1976d2' }) => (
  <Card sx={{ height: '100%', minWidth: 170, flex: '1 1 170px' }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.7, fontSize: 40, display: 'flex' }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const TARGET_COLOR: Record<RedlinkTargetType, 'primary' | 'secondary' | 'success'> = {
  plx: 'primary',
  webinar: 'secondary',
  livestream: 'success',
};

const targetChip = (t: RedlinkTargetType) => (
  <Chip size="small" variant="outlined" color={TARGET_COLOR[t]} label={REDLINK_TARGET_LABEL[t]} />
);

/** CTA-Klicks: `null` heisst NICHT ERHOBEN — als Strich rendern, nie als 0. */
const fmtCta = (v: number | null): string => (v === null ? '–' : fmtInt(v));

const RedlinkEvaluationTab: React.FC = () => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [data, setData] = useState<RedlinkEvaluationResponse | null>(null);
  const [timeline, setTimeline] = useState<RedlinkTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<RedlinkEvaluation | null>(null);
  const [detail, setDetail] = useState<RedlinkDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load list + timeline (cancellation flag — last filter change wins).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams();
        if (startDate && endDate) {
          qs.append('startDate', startDate);
          qs.append('endDate', endDate);
        }
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const [listRes, timelineRes] = await Promise.all([
          fetchData<RedlinkEvaluationResponse>(
            `/api/newsletter-redlinks/evaluation/redlinks${suffix}`,
          ),
          fetchData<RedlinkTimelineResponse>(
            `/api/newsletter-redlinks/evaluation/summary${suffix}`,
          ),
        ]);
        if (cancelled) return;
        if (listRes.success && listRes.data) setData(listRes.data);
        else setError('Fehler beim Laden der Auswertung');
        if (timelineRes.success && timelineRes.data) setTimeline(timelineRes.data);
      } catch (err) {
        if (!cancelled) setError('Fehler beim Laden der Auswertung');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  // Load detail for the selected redlink.
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const qs = new URLSearchParams();
        if (startDate && endDate) {
          qs.append('startDate', startDate);
          qs.append('endDate', endDate);
        }
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const res = await fetchData<RedlinkDetailResponse>(
          `/api/newsletter-redlinks/${selected.id}/evaluation${suffix}`,
        );
        if (!cancelled && res.success && res.data) setDetail(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, startDate, endDate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const summary = data?.summary;
  const redlinks = data?.redlinks ?? [];
  const hasWebinar = redlinks.some((r) => r.targetType === 'webinar');
  const hasLivestream = redlinks.some((r) => r.targetType === 'livestream');
  // Spaltenzahl berechnet statt verschachteltes Ternaer — eine weitere
  // bedingte Spalte wuerde die Zahl sonst still falsch machen.
  const listColSpan = 12 + (hasWebinar ? 1 : 0) + (hasLivestream ? 1 : 0);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Empfänger &amp; Öffnungen</strong> stammen aus der Newsletter-Iterable-Kampagne —
        der Redlink geht an die gesamte Auflage, das ist also die echte Reichweite.{' '}
        <strong>Redlink-Klicks</strong> zählen Klicks auf den Redlink-Link selbst (getrackt über den{' '}
        <code>_RDL_ID-</code>-Token). <strong>Newsletter-Klicks</strong> (im Detail) sind alle Links
        im Newsletter und <em>nicht</em> Redlink-spezifisch. <strong>CPA-Umsatz</strong> = Summe der
        CPA-Werte der ANFOs (jede mit ihrem eigenen Wert); <strong>CPA-ETKP</strong> = CPA-Umsatz / Empfänger × 1000. Zeitfilter grenzt nach{' '}
        <strong>Sendedatum</strong> ein.
        {hasWebinar && (
          <>
            {' '}
            <br />
            Webinar-Redlinks werden über den affiliate-Tag (Registrierung → Bestellung) attribuiert
            — noch nicht mit Live-Daten verifiziert, Werte ggf. unvollständig.
          </>
        )}
        {hasLivestream && (
          <>
            {' '}
            <br />
            <strong>Live-Sendungs-Redlinks</strong> werden über den <code>rdl=</code>-Parameter
            attribuiert: der Live-Player reicht ihn auf die CTA-Ziel-URL weiter, ANFOs kommen
            deshalb aus <code>plx_all_orders.offerurl</code> statt aus <code>banner</code>.{' '}
            <strong>CTA-Klicks</strong> sind Klicks auf der Live-Seite selbst — ohne die
            Auto-Weiterleitung am Ende des Werbevideos und ohne den Iframe-Wechselbanner. Sie
            stammen aus der lokalen DB; „–“ heißt <em>nicht erhoben</em>, nicht „keine“.
          </>
        )}
      </Alert>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      {/* Date filters (by send date) */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Von (Sendedatum)"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Bis (Sendedatum)"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {(startDate || endDate) && (
          <Chip
            label="Filter zurücksetzen"
            onDelete={() => {
              setStartDate('');
              setEndDate('');
            }}
            size="small"
          />
        )}
      </Box>

      {/* Summary cards */}
      {summary && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <StatCard
            title="Empfänger (brutto)"
            value={fmtInt(summary.totalRecipients)}
            icon={<PeopleOutlineIcon fontSize="inherit" />}
          />
          <StatCard
            title="Ø Öffnungsrate"
            value={fmtPct(summary.avgOpenRate)}
            icon={<DraftsOutlinedIcon fontSize="inherit" />}
            color="#0288d1"
          />
          <StatCard
            title="Redlink-Klicks"
            value={`${fmtInt(summary.redlinkClicks)} (${fmtPct(summary.redlinkCtr)})`}
            icon={<TouchAppOutlinedIcon fontSize="inherit" />}
            color="#7b1fa2"
          />
          {hasWebinar && (
            <StatCard
              title="Registrierungen"
              value={fmtInt(summary.totalRegistrations)}
              icon={<HowToRegOutlinedIcon fontSize="inherit" />}
              color="#00897b"
            />
          )}
          {hasLivestream && (
            <StatCard
              title="CTA-Klicks (Live)"
              value={fmtCta(summary.ctaClicks)}
              icon={<TouchAppOutlinedIcon fontSize="inherit" />}
              color="#f9a825"
            />
          )}
          <StatCard
            title="ANFOs"
            value={fmtInt(summary.totalAnfos)}
            icon={<ShoppingCartOutlinedIcon fontSize="inherit" />}
            color="#388e3c"
          />
          <StatCard
            title="CPA-Umsatz"
            value={fmtEur(summary.totalCpaRevenue)}
            icon={<EuroOutlinedIcon fontSize="inherit" />}
            color="#f57c00"
          />
          <StatCard
            title="CPA-ETKP"
            value={fmtEur(summary.cpaEtkp)}
            icon={<TrendingUpOutlinedIcon fontSize="inherit" />}
            color="#c62828"
          />
          <StatCard
            title="Umsatz (real)"
            value={fmtEur(summary.totalRevenue)}
            icon={<EuroOutlinedIcon fontSize="inherit" />}
            color="#5d4037"
          />
        </Box>
      )}

      {/* Per-redlink table */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Redlinks
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEADER_CELL}>Concept</TableCell>
              <TableCell sx={HEADER_CELL}>Ziel</TableCell>
              <TableCell sx={HEADER_CELL}>Produkt/Webinar/Sendung</TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                Sends
              </TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                Empfänger
              </TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                Ø Öffn.
              </TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                Redlink-Klicks
              </TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                CTR
              </TableCell>
              {hasWebinar && (
                <TableCell sx={HEADER_CELL} align="right">
                  Reg.
                </TableCell>
              )}
              {hasLivestream && (
                <TableCell sx={HEADER_CELL} align="right">
                  CTA-Klicks
                </TableCell>
              )}
              <TableCell sx={HEADER_CELL} align="right">
                ANFOs
              </TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                CPA-Umsatz
              </TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                CPA-ETKP
              </TableCell>
              <TableCell sx={HEADER_CELL} align="right">
                Letzter Versand
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {redlinks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={listColSpan} align="center">
                  Keine gesendeten Redlinks im Zeitraum
                </TableCell>
              </TableRow>
            ) : (
              redlinks.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelected(r)}
                >
                  <TableCell>{r.concept}</TableCell>
                  <TableCell>{targetChip(r.targetType)}</TableCell>
                  <TableCell>
                    {r.targetType === 'livestream'
                      ? r.productName
                        ? `Live-Sendung · ${r.productName}`
                        : 'Live-Sendung'
                      : r.productName || r.webinarTitle || '–'}
                  </TableCell>
                  <TableCell align="right">{fmtInt(r.sendCount)}</TableCell>
                  <TableCell align="right">{fmtInt(r.totalRecipients)}</TableCell>
                  <TableCell align="right">{fmtPct(r.avgOpenRate)}</TableCell>
                  <TableCell align="right">{fmtInt(r.redlinkClicks)}</TableCell>
                  <TableCell align="right">{fmtPct(r.redlinkCtr)}</TableCell>
                  {hasWebinar && (
                    <TableCell align="right">{fmtInt(r.totalRegistrations)}</TableCell>
                  )}
                  {hasLivestream && <TableCell align="right">{fmtCta(r.ctaClicks)}</TableCell>}
                  <TableCell align="right">{fmtInt(r.totalAnfos)}</TableCell>
                  <TableCell align="right">{fmtEur(r.totalCpaRevenue)}</TableCell>
                  <TableCell align="right">{fmtEur(r.cpaEtkp)}</TableCell>
                  <TableCell align="right">{fmtDate(r.lastSentAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Per-send-day timeline (one redlink per day) */}
      {timeline && timeline.daily.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Timeline (pro Sendetag)
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEADER_CELL}>Sendetag</TableCell>
                  <TableCell sx={HEADER_CELL}>Concept</TableCell>
                  <TableCell sx={HEADER_CELL}>Ziel</TableCell>
                  <TableCell sx={HEADER_CELL} align="right">
                    Empfänger
                  </TableCell>
                  <TableCell sx={HEADER_CELL} align="right">
                    Ø Öffn.
                  </TableCell>
                  <TableCell sx={HEADER_CELL} align="right">
                    Redlink-Klicks
                  </TableCell>
                  <TableCell sx={HEADER_CELL} align="right">
                    ANFOs
                  </TableCell>
                  <TableCell sx={HEADER_CELL} align="right">
                    CPA-Umsatz
                  </TableCell>
                  <TableCell sx={HEADER_CELL} align="right">
                    CPA-ETKP
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...timeline.daily]
                  .sort((a, b) => b.sendDate.localeCompare(a.sendDate))
                  .map((d) => (
                    <TableRow key={`${d.redlinkId}-${d.sendDate}`} hover>
                      <TableCell>{fmtDate(d.sendDate)}</TableCell>
                      <TableCell>{d.concept}</TableCell>
                      <TableCell>{targetChip(d.targetType)}</TableCell>
                      <TableCell align="right">{fmtInt(d.recipients)}</TableCell>
                      <TableCell align="right">{fmtPct(d.openRate)}</TableCell>
                      <TableCell align="right">{fmtInt(d.redlinkClicks)}</TableCell>
                      <TableCell align="right">{fmtInt(d.anfos)}</TableCell>
                      <TableCell align="right">{fmtEur(d.cpaRevenue)}</TableCell>
                      <TableCell align="right">{fmtEur(d.cpaEtkp)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Detail dialog — per-send-day drill-down */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {selected?.concept} — {selected?.headline}
          <IconButton
            onClick={() => setSelected(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Ziel: {REDLINK_TARGET_LABEL[detail.redlink.targetType]} ·{' '}
                  {detail.redlink.productName || detail.redlink.webinarTitle || '–'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fmtInt(detail.totals.sendCount)} Sends · {fmtInt(detail.totals.totalAnfos)} ANFOs
                  {detail.redlink.targetType === 'webinar' &&
                    ` · ${fmtInt(detail.totals.totalRegistrations)} Reg.`}
                  {detail.redlink.targetType === 'livestream' &&
                    ` · ${fmtCta(detail.totals.ctaClicks)} CTA-Klicks`}{' '}
                  · CPA-Umsatz {fmtEur(detail.totals.totalCpaRevenue)} · CPA-ETKP{' '}
                  {fmtEur(detail.totals.cpaEtkp)}
                </Typography>
              </Box>
              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={HEADER_CELL}>Sendetag</TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        Empfänger
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        Öffnungen
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        Öffn. %
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        Redlink-Klicks
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        CTR
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        Newsletter-Klicks
                      </TableCell>
                      {detail.redlink.targetType === 'webinar' && (
                        <TableCell sx={HEADER_CELL} align="right">
                          Reg.
                        </TableCell>
                      )}
                      {detail.redlink.targetType === 'livestream' && (
                        <TableCell sx={HEADER_CELL} align="right">
                          CTA-Klicks
                        </TableCell>
                      )}
                      <TableCell sx={HEADER_CELL} align="right">
                        ANFOs
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        CPA-Umsatz
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        Umsatz real
                      </TableCell>
                      <TableCell sx={HEADER_CELL} align="right">
                        CPA-ETKP
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.sends.map((s) => (
                      <TableRow key={s.sendDate} hover>
                        <TableCell>{fmtDate(s.sendDate)}</TableCell>
                        <TableCell align="right">{fmtInt(s.recipients)}</TableCell>
                        <TableCell align="right">{fmtInt(s.opens)}</TableCell>
                        <TableCell align="right">{fmtPct(s.openRate)}</TableCell>
                        <TableCell align="right">{fmtInt(s.redlinkClicks)}</TableCell>
                        <TableCell align="right">{fmtPct(s.redlinkCtr)}</TableCell>
                        <TableCell align="right">{fmtInt(s.newsletterClicks)}</TableCell>
                        {detail.redlink.targetType === 'webinar' && (
                          <TableCell align="right">{fmtInt(s.registrations)}</TableCell>
                        )}
                        {detail.redlink.targetType === 'livestream' && (
                          <TableCell align="right">{fmtCta(s.ctaClicks)}</TableCell>
                        )}
                        <TableCell align="right">{fmtInt(s.anfos)}</TableCell>
                        <TableCell align="right">{fmtEur(s.cpaRevenue)}</TableCell>
                        <TableCell align="right">{fmtEur(s.revenue)}</TableCell>
                        <TableCell align="right">{fmtEur(s.cpaEtkp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RedlinkEvaluationTab;
